import React, { useState } from "react";
import { RotateCcw, Trash2 } from "react-feather";
import { useTheme } from "../theme/ThemeContext";
import { useAuth } from "../auth/AuthContext";
import { API_BASE, apiFetch } from "../api";
import { Card, Button, Field, useInputStyle } from "../mobile/ui";

// What each action removes, and what it's called.
const ACTIONS = {
  reset: {
    title: "Reset data",
    Icon: RotateCcw,
    path: "account/reset",
    explain:
      "Deletes every expense, bill, money received, bank account, card, loan, investment and budget you've added, so you can start fresh. Your account, settings, tasks and groups stay.",
    confirm: "Reset all my data",
  },
  delete: {
    title: "Delete account",
    Icon: Trash2,
    path: "account/delete",
    explain:
      "Deletes your account and everything in it, for good. Tasks you created are deleted for everyone on them; you're taken off other people's tasks and groups.",
    confirm: "Delete my account",
  },
};

// Reset data / delete account, each behind the account password (neither can be
// undone). Used on the phone's You screen and the desktop Settings page.
// `onReset` runs after a reset (to reload); `beforeLogout` runs after the account is
// deleted, before signing out (the phone forgets its reminders and device there).
const DangerZone = ({ onReset, beforeLogout }) => {
  const { tokens } = useTheme();
  const { logout } = useAuth();
  const input = useInputStyle();
  const [open, setOpen] = useState(null); // null | "reset" | "delete"
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  const choose = (id) => {
    setOpen(open === id ? null : id);
    setPassword("");
    setError("");
    setDone("");
  };

  const run = async () => {
    if (!password) return setError("Enter your password to confirm.");
    setBusy(true);
    setError("");
    try {
      const res = await apiFetch(`${API_BASE}/${ACTIONS[open].path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Something went wrong.");
      if (open === "delete") {
        if (beforeLogout) await beforeLogout();
        logout();
        return;
      }
      setOpen(null);
      setPassword("");
      setDone("Your data was reset.");
      if (onReset) onReset();
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };

  return (
    <Card>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {Object.entries(ACTIONS).map(([id, a]) => (
          <div key={id}>
            <Button kind="danger" onClick={() => choose(id)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <a.Icon size={16} /> {a.title}
            </Button>
            {open === id && (
              <div style={{ marginTop: 10, padding: 12, borderRadius: 12, backgroundColor: tokens.neutralSoft }}>
                <p style={{ margin: "0 0 10px", fontSize: 13, color: tokens.textPrimary }}>{a.explain} This can't be undone.</p>
                <Field label="Your password">
                  <input style={input} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
                </Field>
                {error && <div role="alert" style={{ marginBottom: 10, fontSize: 13, color: tokens.negative }}>{error}</div>}
                <div style={{ display: "flex", gap: 10 }}>
                  <Button kind="danger" disabled={busy} onClick={run} style={{ flex: 1 }}>{busy ? "Working…" : a.confirm}</Button>
                  <Button kind="ghost" disabled={busy} onClick={() => choose(id)} style={{ flex: 1 }}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        ))}
        {done && <div role="status" style={{ fontSize: 13, color: tokens.positive }}>{done}</div>}
      </div>
    </Card>
  );
};

export default DangerZone;
